import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { BRAND } from '../config/brand.js';

/**
 * Per-company branding at runtime. On boot it resolves the tenant and fetches
 * GET /api/tenant/branding, then repaints the app by overriding the brand CSS
 * variables on :root (so every component styled with var(--brand-*) re-skins
 * with zero per-component changes) and swaps the document title + favicon.
 *
 * Tenant resolution: in production the API resolves by the request Host (each
 * company on its own domain), so nothing extra is needed. In dev (localhost)
 * pass ?tenant=<slug> once; it's remembered in localStorage and sent as a hint.
 */
const TenantContext = createContext(null);
const TENANT_KEY = 'afc_tenant';

const DEFAULT_BRAND = {
  name: BRAND.name,
  shortName: BRAND.shortName,
  tagline: BRAND.tagline,
  logoUrl: null,
  logo: BRAND.logo,
  isTenant: false,
};

function devTenantHint() {
  const q = new URLSearchParams(window.location.search).get('tenant');
  if (q) {
    localStorage.setItem(TENANT_KEY, q);
    return q;
  }
  return localStorage.getItem(TENANT_KEY) || null;
}

function applyBranding(branding) {
  const root = document.documentElement;
  const set = (name, val) => val && root.style.setProperty(name, val);
  set('--brand-primary', branding?.primaryColor);
  set('--brand-info', branding?.infoColor);
  set('--brand-success', branding?.successColor);
  set('--brand-danger', branding?.dangerColor);
  set('--text-strong', branding?.textColor);

  // The tab icon follows whichever branding asset the admin actually set
  // (QA 7981). Branding Management exposes only Logo and Home-screen icon —
  // there is no favicon field — so favicon_url is always null in practice and
  // the tab kept the platform default no matter what was uploaded. Prefer the
  // home icon over the logo: processHomeIcon squares it off, whereas logos are
  // typically wide and squash badly at 16px.
  const iconUrl = branding?.faviconUrl || branding?.homeIconUrl || branding?.logoUrl;
  if (iconUrl) {
    let link = document.querySelector('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    // Drop the static type="image/svg+xml" from index.html — the uploaded asset
    // is usually a PNG/JPEG data URI and Chrome ignores a mistyped icon.
    link.removeAttribute('type');
    link.href = iconUrl;
  }

  if (branding?.homeIconUrl) {
    let apple = document.querySelector('link[rel="apple-touch-icon"]');
    if (!apple) {
      apple = document.createElement('link');
      apple.rel = 'apple-touch-icon';
      document.head.appendChild(apple);
    }
    apple.href = branding.homeIconUrl;
  }
}

export function TenantProvider({ children }) {
  const [state, setState] = useState({ loading: true, tenant: null, branding: null });

  useEffect(() => {
    const hint = devTenantHint();
    const url = `/api/tenant/branding${hint ? `?tenant=${encodeURIComponent(hint)}` : ''}`;
    let active = true;

    fetch(url, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : { data: { tenant: null, branding: null } }))
      .then((res) => {
        if (!active) return;
        const { tenant = null, branding = null } = res.data || {};
        applyBranding(branding);
        if (branding?.displayName) document.title = branding.displayName;
        setState({ loading: false, tenant, branding });
      })
      .catch(() => active && setState({ loading: false, tenant: null, branding: null }));

    return () => {
      active = false;
    };
  }, []);

  const brand = useMemo(() => {
    const b = state.branding;
    if (!b) return DEFAULT_BRAND;
    return {
      name: b.displayName || BRAND.name,
      // A tenant that set only a display name should still brand with it — fall
      // back to the display name (not the platform default) when short name is blank.
      shortName: b.shortName || b.displayName || BRAND.shortName,
      tagline: b.tagline || BRAND.tagline,
      logoUrl: b.logoUrl || null,
      logo: BRAND.logo,
      isTenant: true,
    };
  }, [state.branding]);

  const value = useMemo(() => ({ ...state, brand }), [state, brand]);
  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

/** Full tenant context. Safe outside a provider (returns platform defaults). */
export function useTenant() {
  return (
    useContext(TenantContext) || { loading: false, tenant: null, branding: null, brand: DEFAULT_BRAND }
  );
}

/** Just the resolved brand (tenant override merged over platform defaults). */
export function useBrand() {
  return useTenant().brand;
}

export default TenantProvider;
